import React, { useState } from "react";
import { Link } from "react-router-dom";
import AttractionsChart from "../components/Charts/AttractionsChart";
import ScrollReveal, { RevealUp, RevealLeft, RevealRight, RevealScale, StaggeredReveal } from '../components/ScrollReveal';

const About = () => {
  const [activeTab, setActiveTab] = useState("history");

  // Historical Timeline Data
  const timelineData = [
    {
      era: "त्रेता युग",
      event: "राम-सीता प्रथम मिलन",
      description: "पुष्प वाटिका में भगवान राम और माता सीता का पहला दर्शन",
      significance: "प्रेम और भक्ति का उद्गम स्थल",
    },
    {
      era: "द्वापर युग",
      event: "गिरिजा मंदिर स्थापना",
      description: "देवी पार्वती के मंदिर की स्थापना",
      significance: "विवाह पूर्व पूजा की परंपरा",
    },
    {
      era: "मध्यकाल",
      event: "मिथिला चित्रकला विकास",
      description: "पुष्प वाटिका से प्रेरित कलाकृतियों का जन्म",
      significance: "सांस्कृतिक धरोहर का संरक्षण",
    },
    {
      era: "आधुनिक काल (2025)",
      event: "ट्रस्ट स्थापना व विकास",
      description: "₹31.13 करोड़ की विकास योजना स्वीकृत",
      significance: "वैश्विक तीर्थ स्थल का रूप",
    },
  ];

  // Distance and Transport Data
  const transportData = [
    {
      destination: "मधुबनी शहर",
      distance: "40-48 किमी",
      transport: "बस, टैक्सी, ऑटो",
      time: "1-1.5 घंटे",
    },
    {
      destination: "जनकपुर (नेपाल)",
      distance: "15-20 किमी",
      transport: "सड़क मार्ग",
      time: "30-45 मिनट",
    },
    {
      destination: "दरभंगा एयरपोर्ट",
      distance: "65-70 किमी",
      transport: "टैक्सी, बस",
      time: "1.5-2 घंटे",
    },
    {
      destination: "सीतामढ़ी",
      distance: "45-50 किमी",
      transport: "बस (NH-227)",
      time: "1-1.5 घंटे",
    },
    {
      destination: "जयनगर रेलवे स्टेशन",
      distance: "25-30 किमी",
      transport: "बस, ट्रेन",
      time: "45 मिनट-1 घंटे",
    },
  ];

  // Festival Calendar Data
  const festivalData = [
    {
      festival: "मिथिला मध्य परिक्रमा",
      month: "फाल्गुन (फरवरी-मार्च)",
      duration: "15 दिन",
      significance: "मिथिला का महाकुंभ",
      visitors: "लाखों श्रद्धालु",
    },
    {
      festival: "विवाह पंचमी",
      month: "अगहन (नवंबर-दिसंबर)",
      duration: "1 दिन",
      significance: "राम-सीता विवाह उत्सव",
      visitors: "हजारों भक्त",
    },
    {
      festival: "राम नवमी",
      month: "चैत्र (मार्च-अप्रैल)",
      duration: "1 दिन",
      significance: "भगवान राम जन्मोत्सव",
      visitors: "हजारों भक्त",
    },
    {
      festival: "जानकी नवमी",
      month: "वैशाख (अप्रैल-मई)",
      duration: "1 दिन",
      significance: "माता सीता जन्मोत्सव",
      visitors: "हजारों भक्त",
    },
  ];

  // Development Projects Data
  const developmentData = [
    {
      project: "पुष्प वाटिका एवं गिरिजा स्थान विकास",
      budget: "₹31.13 करोड़",
      status: "चालू",
      completion: "2026",
    },
    {
      project: "मधुबनी अवसंरचना विकास",
      budget: "₹650 करोड़",
      status: "चालू",
      completion: "2027",
    },
    {
      project: "खजौली-जयनगर ROB",
      budget: "₹178 करोड़",
      status: "प्रारंभिक",
      completion: "2026",
    },
    {
      project: "बाग तराग सौंदर्यीकरण",
      budget: "₹5 करोड़",
      status: "चालू",
      completion: "2025",
    },
  ];

  const tabs = [
    { id: "history", label: "इतिहास", icon: "fas fa-scroll" },
    { id: "geography", label: "भूगोल", icon: "fas fa-map" },
    { id: "culture", label: "संस्कृति", icon: "fas fa-palette" },
    { id: "development", label: "विकास", icon: "fas fa-hammer" },
    { id: "festivals", label: "त्योहार", icon: "fas fa-calendar-alt" },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#FEF6E4' }}>
      {/* ══ HERO ══ */}
      <section className="about-ram-sita-bg relative py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none flex items-center justify-center">
          <span style={{ fontSize: '70vw', lineHeight: 1, color: '#F4A500' }}>🕉️</span>
        </div>
        <RevealScale className="relative max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: 'rgba(255,190,46,0.7)' }}>फुलहर धाम</p>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
            फुलहर धाम का ऐतिहासिक सर्वेक्षण
          </h1>
          <p className="text-base sm:text-lg mb-8 max-w-2xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
            सीता राम प्रथम मिलन की पावन भूमि — मिथिला की सांस्कृतिक धरोहर
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/gallery" className="btn-temple-secondary px-8 py-3.5 rounded-2xl font-bold text-sm">📸 फोटो गैलरी</Link>
            <Link to="/donate"  className="btn-donation px-8 py-3.5 rounded-2xl font-bold text-sm">💰 दान करें</Link>
          </div>
        </RevealScale>
      </section>

      {/* ══ TABS ══ */}
      <section className="sticky z-40 border-b" style={{ top: '0', background: 'rgba(254,246,228,0.97)', backdropFilter: 'blur(16px)', borderColor: 'rgba(244,165,0,0.15)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-5">
          <div className="flex overflow-x-auto scrollbar-hide py-4 gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2 rounded-xl font-bold text-sm whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab.id
                    ? "btn-temple-primary shadow-temple"
                    : "bg-temple-bg text-temple-text hover:bg-temple-bg-soft border border-temple-primary/20"
                }`}
              >
                <span className="mr-2">
                  <i className={tab.icon}></i>
                </span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CONTENT ══ */}
      <div className="max-w-5xl mx-auto px-4 sm:px-5 py-8 sm:py-10">
        {activeTab === "history" && (
          <div className="space-y-8">
            <RevealUp className="rounded-3xl p-6 sm:p-8 shadow-temple" style={{ background: '#FAE8C8', border: '1px solid rgba(244,165,0,0.2)' }}>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3">📚</span>
                नामकरण और व्युत्पत्ति
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <RevealLeft>
                  <p className="text-temple-text-muted leading-relaxed mb-4">
                    'फुलहर' शब्द की व्युत्पत्ति मैथिली भाषा के शब्द 'फुलवारी'
                    (Phulwari) से हुई है, जिसका अर्थ है 'फूलों का बगीचा'। समय के
                    साथ 'फुलवारी' शब्द अपभ्रंश होकर 'फुलहर' बन गया।
                  </p>
                  <div className="bg-temple-primary/10 rounded-2xl p-4">
                    <h4 className="font-bold text-temple-primary mb-2">
                      भौगोलिक स्थिति
                    </h4>
                    <ul className="text-sm text-temple-text-muted space-y-1">
                      <li>• अक्षांश: 26.589°N</li>
                      <li>• देशांतर: 85.907°E</li>
                      <li>• समुद्र तल से ऊंचाई: 56 मीटर</li>
                      <li>• भारत-नेपाल सीमा से दूरी: 8 किमी</li>
                    </ul>
                  </div>
                </RevealLeft>
                <RevealRight className="rounded-2xl p-6" style={{ background: '#FDF0D5', border: '1px solid rgba(244,165,0,0.2)' }}>
                  <h4 className="font-bold text-temple-text mb-4">
                    रामायण में वर्णन
                  </h4>
                  <blockquote className="text-temple-text-muted italic border-l-4 border-temple-secondary pl-4">
                    "बाग तड़ाग बिलोकि प्रभु, हरषे बंधु समेत।
                    <br />
                    परम रम्य आराम यह, जो रामहि सुख देत॥"
                  </blockquote>
                  <p className="text-xs text-temple-text-muted mt-3">
                    - गोस्वामी तुलसीदास, श्रीरामचरितमानस
                  </p>
                </RevealRight>
              </div>
            </RevealUp>

            {/* Historical Timeline */}
            <RevealScale className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-8 flex items-center">
                <span className="mr-3 text-temple-primary">
                  <i className="fas fa-history"></i>
                </span>
                ऐतिहासिक कालक्रम
              </h2>
              <div className="space-y-6">
                <StaggeredReveal>
                  {timelineData.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col sm:flex-row gap-6 p-6 bg-temple-bg rounded-2xl border border-temple-primary/10 hover:shadow-temple-sm transition-all duration-300"
                    >
                      <div className="sm:w-32 flex-shrink-0">
                        <div className="bg-temple-primary text-temple-bg px-3 py-1 rounded-full text-sm font-bold text-center">
                          {item.era}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-temple-text mb-2">
                          {item.event}
                        </h4>
                        <p className="text-temple-text-muted mb-3">
                          {item.description}
                        </p>
                        <div className="bg-temple-secondary/20 text-temple-secondary px-3 py-1 rounded-full text-xs font-bold inline-block">
                          {item.significance}
                        </div>
                      </div>
                    </div>
                  ))}
                </StaggeredReveal>
              </div>
            </RevealScale>
          </div>
        )}

        {/* Geography Section */}
        {activeTab === "geography" && (
          <div className="space-y-8 sm:space-y-12">
            {/* Transport Table */}
            <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3 text-temple-primary">
                  <i className="fas fa-car"></i>
                </span>
                परिवहन और दूरी
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full bg-temple-bg rounded-2xl overflow-hidden shadow-temple-sm">
                  <thead className="bg-temple-primary text-temple-bg">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold">गंतव्य</th>
                      <th className="px-4 py-3 text-left font-bold">दूरी</th>
                      <th className="px-4 py-3 text-left font-bold">परिवहन</th>
                      <th className="px-4 py-3 text-left font-bold">समय</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transportData.map((item, index) => (
                      <tr
                        key={index}
                        className={`border-b border-temple-primary/10 ${
                          index % 2 === 0 ? "bg-temple-bg-soft" : "bg-temple-bg"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium text-temple-text">
                          {item.destination}
                        </td>
                        <td className="px-4 py-3 text-temple-text-muted">
                          {item.distance}
                        </td>
                        <td className="px-4 py-3 text-temple-text-muted">
                          {item.transport}
                        </td>
                        <td className="px-4 py-3 text-temple-text-muted">
                          {item.time}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Nearby Sacred Places */}
            <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3 text-temple-primary">
                  <i className="fas fa-om"></i>
                </span>
                आसपास के तीर्थ स्थल
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    name: "कल्याणेश्वर महादेव मंदिर",
                    location: "कलना",
                    description: "राजा जनक द्वारा स्थापित प्राचीन शिव मंदिर",
                  },
                  {
                    name: "विश्वामित्र आश्रम",
                    location: "बिसौल",
                    description: "राम-लक्ष्मण के ठहरने का स्थान",
                  },
                  {
                    name: "मनोकामना नाथ मंदिर",
                    location: "कमतौल",
                    description: "गिरिजा स्थान से संबंधित सिद्ध पीठ",
                  },
                  {
                    name: "परिक्रमा गाछी",
                    location: "मटिहानी",
                    description: "भारत-नेपाल सीमा पर ऐतिहासिक आम का बगीचा",
                  },
                  {
                    name: "राघो बाबा मंदिर",
                    location: "परिक्रमा गाछी",
                    description: "स्थानीय लोक देवता का मंदिर",
                  },
                  {
                    name: "अहिल्या स्थान",
                    location: "अहियारी",
                    description: "अहिल्या उद्धार का स्थान",
                  },
                ].map((place, index) => (
                  <div
                    key={index}
                    className="bg-temple-bg rounded-2xl p-4 border border-temple-primary/10 hover:shadow-temple-sm transition-all duration-300"
                  >
                    <h4 className="font-bold text-temple-text mb-2">
                      {place.name}
                    </h4>
                    <p className="text-sm text-temple-primary font-medium mb-2">
                      📍 {place.location}
                    </p>
                    <p className="text-sm text-temple-text-muted">
                      {place.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Culture Section */}
        {activeTab === "culture" && (
          <div className="space-y-8 sm:space-y-12">
            {/* Mithila Art Connection */}
            <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3">🎨</span>
                मिथिला चित्रकला का उद्गम
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <p className="text-temple-text-muted leading-relaxed mb-6">
                    कला इतिहासकारों के अनुसार, मधुबनी चित्रकला की उत्पत्ति
                    त्रेतायुग में राजा जनक के समय हुई थी। जब राम और सीता का
                    विवाह तय हुआ, तो राजा जनक ने पूरे मिथिला राज्य को चित्रों से
                    सजाने का आदेश दिया था।
                  </p>
                  <div className="space-y-4">
                    <h4 className="font-bold text-temple-text">
                      कला में पुष्प वाटिका के प्रतीक:
                    </h4>
                    <ul className="space-y-2 text-temple-text-muted">
                      <li className="flex items-center">
                        <span className="mr-2">🌸</span>प्रकृति चित्रण: सूर्य,
                        चंद्रमा, तुलसी और वट वृक्ष
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2">👫</span>युगल छवि: राम और सीता का
                        वाटिका में मिलन
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2">🏠</span>कोहबर घर: नवविवाहित
                        जोड़ों के कमरे की सजावट
                      </li>
                      <li className="flex items-center">
                        <span className="mr-2">🪷</span>कमल: प्रजनन और शुद्धता का
                        प्रतीक
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="rounded-2xl p-6" style={{ background: '#FDF0D5', border: '1px solid rgba(244,165,0,0.2)' }}>
                  <h4 className="font-bold text-temple-text mb-4">
                    मधुबनी पेंटिंग की शैलियां
                  </h4>
                  <div className="space-y-3">
                    {[
                      {
                        style: "कचनी (Kachni)",
                        description: "काली स्याही से बनी बारीक रेखाएं",
                      },
                      {
                        style: "भरनी (Bharni)",
                        description: "रंगों से भरे गए चित्र",
                      },
                      { style: "गोदना", description: "टैटू शैली के चित्र" },
                      { style: "कोहबर", description: "विवाह कक्ष की सजावट" },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="bg-temple-bg-soft p-3 rounded-lg"
                      >
                        <h5 className="font-bold text-temple-primary text-sm">
                          {item.style}
                        </h5>
                        <p className="text-xs text-temple-text-muted">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Sacred Architecture */}
            <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3">🏛️</span>
                स्थापत्य और पुरातत्व
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    title: "गिरिजा स्थान मंदिर",
                    description: "प्राचीन गर्भगृह और आधुनिक संरचना का संगम",
                    features: [
                      "देवी गिरिजा की प्रतिमा",
                      "मिथिला स्थापत्य शैली",
                      "गुंबद और शिखर",
                    ],
                  },
                  {
                    title: "बाग तराग (सरोवर)",
                    description: "रामायण में वर्णित पवित्र सरोवर",
                    features: [
                      "350 मीटर दूरी पर स्थित",
                      "पवित्र जल",
                      "सौंदर्यीकरण कार्य",
                    ],
                  },
                  {
                    title: "प्रथम मिलन मंदिर",
                    description: "राम-सीता के प्रथम दर्शन का स्थान",
                    features: [
                      "श्री राम चरणारविंद",
                      "प्रेम का उद्गम स्थल",
                      "विशेष पूजा स्थल",
                    ],
                  },
                ].map((place, index) => (
                  <div
                    key={index}
                    className="bg-temple-bg rounded-2xl p-6 border border-temple-primary/10"
                  >
                    <h4 className="font-bold text-temple-text mb-3">
                      {place.title}
                    </h4>
                    <p className="text-sm text-temple-text-muted mb-4">
                      {place.description}
                    </p>
                    <ul className="space-y-1">
                      {place.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="text-xs text-temple-text-muted flex items-center"
                        >
                          <span className="mr-2 text-temple-secondary">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Development Section */}
        {activeTab === "development" && (
          <div className="space-y-8 sm:space-y-12">
            {/* Charts and Graphs */}
            <AttractionsChart />

            {/* Development Projects Table */}
            <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3">🏗️</span>
                विकास परियोजनाएं (2025-2027)
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full bg-temple-bg rounded-2xl overflow-hidden shadow-temple-sm">
                  <thead className="bg-temple-primary text-temple-bg">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold">
                        परियोजना
                      </th>
                      <th className="px-4 py-3 text-left font-bold">बजट</th>
                      <th className="px-4 py-3 text-left font-bold">स्थिति</th>
                      <th className="px-4 py-3 text-left font-bold">पूर्णता</th>
                    </tr>
                  </thead>
                  <tbody>
                    {developmentData.map((project, index) => (
                      <tr
                        key={index}
                        className={`border-b border-temple-primary/10 ${
                          index % 2 === 0 ? "bg-temple-bg-soft" : "bg-temple-bg"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium text-temple-text">
                          {project.project}
                        </td>
                        <td className="px-4 py-3 text-temple-primary font-bold">
                          {project.budget}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-bold ${
                              project.status === "चालू"
                                ? "bg-green-100 text-green-700"
                                : project.status === "प्रारंभिक"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {project.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-temple-text-muted">
                          {project.completion}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Government Initiatives */}
            <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3">🏛️</span>
                सरकारी पहल और घोषणाएं
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-temple-text mb-4">
                    प्रगति यात्रा (12 जनवरी 2025)
                  </h4>
                  <p className="text-temple-text-muted mb-4">
                    मुख्यमंत्री नीतीश कुमार ने अपनी 'प्रगति यात्रा' के दौरान
                    फुलहर को 'रामायण सर्किट' का अभिन्न अंग घोषित किया और इसे
                    पर्यटन मानचित्र पर स्थापित करने की घोषणा की।
                  </p>
                  <div className="bg-temple-primary/10 rounded-2xl p-4">
                    <h5 className="font-bold text-temple-primary mb-2">
                      मुख्य घोषणाएं:
                    </h5>
                    <ul className="text-sm text-temple-text-muted space-y-1">
                      <li>• ₹31.13 करोड़ का विकास फंड</li>
                      <li>• रामायण सर्किट में शामिल</li>
                      <li>• पर्यटन अवसंरचना विकास</li>
                      <li>• स्थानीय रोजगार सृजन</li>
                    </ul>
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-temple-text mb-4">
                    वास्तुशिल्प समीक्षा (15 जून 2025)
                  </h4>
                  <p className="text-temple-text-muted mb-4">
                    'स्टैच्यू ऑफ यूनिटी' के शिल्पकार पद्म भूषण डॉ. राम वनजी
                    सुतार और वास्तुकार रिम्पेश शर्मा ने फुलहर का दौरा किया।
                  </p>
                  <div className="bg-temple-secondary/10 rounded-2xl p-4">
                    <h5 className="font-bold text-temple-secondary mb-2">
                      भविष्य की योजनाएं:
                    </h5>
                    <ul className="text-sm text-temple-text-muted space-y-1">
                      <li>• राम-सीता की भव्य प्रतिमाएं</li>
                      <li>• विश्वस्तरीय स्मारक</li>
                      <li>• अयोध्या के समकक्ष विकास</li>
                      <li>• अंतर्राष्ट्रीय पर्यटन केंद्र</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Festivals Section */}
        {activeTab === "festivals" && (
          <div className="space-y-8 sm:space-y-12">
            {/* Festival Calendar */}
            <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3">🎉</span>
                त्योहार कैलेंडर
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full bg-temple-bg rounded-2xl overflow-hidden shadow-temple-sm">
                  <thead className="bg-temple-primary text-temple-bg">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold">त्योहार</th>
                      <th className="px-4 py-3 text-left font-bold">समय</th>
                      <th className="px-4 py-3 text-left font-bold">अवधि</th>
                      <th className="px-4 py-3 text-left font-bold">महत्व</th>
                      <th className="px-4 py-3 text-left font-bold">
                        श्रद्धालु
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {festivalData.map((festival, index) => (
                      <tr
                        key={index}
                        className={`border-b border-temple-primary/10 ${
                          index % 2 === 0 ? "bg-temple-bg-soft" : "bg-temple-bg"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium text-temple-text">
                          {festival.festival}
                        </td>
                        <td className="px-4 py-3 text-temple-text-muted">
                          {festival.month}
                        </td>
                        <td className="px-4 py-3 text-temple-text-muted">
                          {festival.duration}
                        </td>
                        <td className="px-4 py-3 text-temple-text-muted">
                          {festival.significance}
                        </td>
                        <td className="px-4 py-3 text-temple-primary font-medium">
                          {festival.visitors}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mithila Madhya Parikrama Details */}
            <div className="bg-temple-bg-warm rounded-3xl p-6 sm:p-8 shadow-temple border border-temple-primary/10">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text mb-6 flex items-center">
                <span className="mr-3">🚶‍♂️</span>
                मिथिला मध्य परिक्रमा - विस्तृत विवरण
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-temple-text mb-4">
                    परिक्रमा मार्ग
                  </h4>
                  <div className="space-y-3">
                    {[
                      "कचुरी (नेपाल) - प्रारंभ",
                      "कल्याणेश्वर स्थान (कलना)",
                      "गिरिजा स्थान (फुलहर) - रात्रि विश्राम",
                      "मटिहानी (नेपाल सीमा)",
                      "जनकपुर (नेपाल) - समापन",
                    ].map((place, index) => (
                      <div
                        key={index}
                        className="flex items-center p-3 bg-temple-bg rounded-lg border border-temple-primary/10"
                      >
                        <div className="w-8 h-8 bg-temple-primary text-temple-bg rounded-full flex items-center justify-center font-bold text-sm mr-3">
                          {index + 1}
                        </div>
                        <span className="text-temple-text-muted">{place}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-bold text-temple-text mb-4">
                    परिक्रमा की विशेषताएं
                  </h4>
                  <div className="space-y-4">
                    <div className="bg-temple-bg rounded-2xl p-4 border border-temple-secondary/20">
                      <h5 className="font-bold text-temple-secondary mb-2">
                        🗓️ समय सारणी
                      </h5>
                      <p className="text-sm text-temple-text-muted">
                        फाल्गुन अमावस्या से होली तक (15 दिन)
                        <br />
                        2025 में: 27 फरवरी से 14 मार्च
                      </p>
                    </div>
                    <div className="bg-temple-bg rounded-2xl p-4 border border-temple-secondary/20">
                      <h5 className="font-bold text-temple-secondary mb-2">
                        👥 सहभागिता
                      </h5>
                      <p className="text-sm text-temple-text-muted">
                        भारत और नेपाल से लाखों श्रद्धालु
                        <br />
                        राम-सीता का डोला (पालकी) यात्रा
                      </p>
                    </div>
                    <div className="bg-temple-bg rounded-2xl p-4 border border-temple-secondary/20">
                      <h5 className="font-bold text-temple-secondary mb-2">
                        🏛️ फुलहर में गतिविधियां
                      </h5>
                      <p className="text-sm text-temple-text-muted">
                        पुष्प वाटिका दर्शन
                        <br />
                        गिरिजा स्थान पूजा
                        <br />
                        रात्रि विश्राम और भजन-कीर्तन
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══ CTA ══ */}
      <section className="py-16 sm:py-20 swayamvar-bg relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none flex items-center justify-center">
          <span style={{ fontSize: '60vw', lineHeight: 1, color: '#F4A500' }}>🕉️</span>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl mb-6">🙏</div>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
            फुलहर धाम की यात्रा करें
          </h2>
          <p className="text-sm sm:text-base mb-8 max-w-2xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.8)' }}>
            राम-सीता के प्रथम मिलन की पावन भूमि का दर्शन करें और मिथिला की समृद्ध संस्कृति का अनुभव लें।
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-temple-secondary px-8 py-3.5 rounded-2xl font-bold text-sm">📞 यात्रा की जानकारी</Link>
            <Link to="/donate"  className="btn-donation   px-8 py-3.5 rounded-2xl font-bold text-sm">💰 विकास में योगदान</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
